import type { LoggerService } from '@/services/abstract/LoggerService';
import type { Libp2pService } from '@/services/abstract/poc/Libp2pService';
import {
  type FileDeletedResult,
  type FolderCreatedResult,
  type FolderDeletedResult,
  type MessageHandlerResult,
  type MessageStats,
  type SixCreatedResult,
  type SixDeletedResult,
  type SixUpdatedResult,
  MessagingService,
} from '@/services/abstract/poc/MessagingService';
import { TOPIC_LIST_TOPIC } from '@/config/networkConstants';
import type { Logger } from '@/types/Logger';
import type { HeliaNode } from '@/types/poc/helia';
import type { LibP2pWithServices } from '@/types/poc/libp2p';
import type { MessageHandler, MessagePayload } from '@/types/poc/message';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isMessagePayload = (value: unknown): value is MessagePayload =>
  isRecord(value) &&
  typeof value.type === 'string' &&
  typeof value.sender === 'string' &&
  typeof value.id === 'string' &&
  typeof value.timestamp === 'number';

const isFolderCreatedMessage = (
  value: MessagePayload
): value is MessagePayload & {
  type: 'folder-created';
  sixId: string;
  folderName: string;
  folderPath: string;
} => value.type === 'folder-created';

const isFolderDeletedMessage = (
  value: MessagePayload
): value is MessagePayload & {
  type: 'folder-deleted';
  sixId: string;
  folderPath: string;
} => value.type === 'folder-deleted';

const isFileDeletedMessage = (
  value: MessagePayload
): value is MessagePayload & {
  type: 'file-deleted';
  sixId: string;
  fileId: string;
} => value.type === 'file-deleted';

const isSixCreatedMessage = (
  value: MessagePayload
): value is MessagePayload & {
  type: 'six-created';
} => value.type === 'six-created';

const isSixUpdatedMessage = (
  value: MessagePayload
): value is MessagePayload & {
  type: 'six-updated';
} => value.type === 'six-updated';

const isSixDeletedMessage = (
  value: MessagePayload
): value is MessagePayload & {
  type: 'six-deleted';
} => value.type === 'six-deleted';

export class MessagingServiceImpl extends MessagingService {
  private readonly log: Logger;
  private topics: string[] = [];

  constructor(
    loggerService: LoggerService,
    private readonly libp2pService: Libp2pService,
    private readonly handlers: MessageHandler[]
  ) {
    super();
    this.log = loggerService.createLogger({ namespace: 'six3ro:p2p:messaging' });
  }

  setupSubscriptions(
    libp2pNode: LibP2pWithServices,
    heliaNode: HeliaNode | null,
    onMessage: (result: MessageHandlerResult, stats: MessageStats) => void,
    topics: string[]
  ): () => void {
    const unsubscribeFunctions: Array<() => void> = [];
    this.topics = topics;
    this.log.info(`Setting up subscriptions for topics: ${topics.join(', ')}`);

    const handleIncomingMessage = async(data: Uint8Array, topic: string): Promise<void> => {
      const decodedMessage = new TextDecoder().decode(data);
      this.log.debug(`Message received on topic ${topic}: ${decodedMessage}`);

      let parsed: unknown = null;
      try {
        parsed = JSON.parse(decodedMessage);
      } catch {
        return;
      }

      if (!isRecord(parsed)) {
        return;
      }

      const parsedRecord = parsed;
      if (topic.includes('mesh-heartbeat') && parsedRecord.source === 'hap') {
        this.log.debug('Received HAP mesh heartbeat');
        return;
      }

      if (!isMessagePayload(parsedRecord)) {
        return;
      }

      const stats: MessageStats = {
        fromPeer:
          typeof parsedRecord.sender === 'string'
            ? parsedRecord.sender
            : typeof parsedRecord.from === 'string'
              ? parsedRecord.from
              : typeof parsedRecord.peerId === 'string'
                ? parsedRecord.peerId
                : 'unknown',
        topic,
        timestamp:
          typeof parsedRecord.timestamp === 'number' ? parsedRecord.timestamp : Date.now(),
        messageType:
          typeof parsedRecord.type === 'string' ? parsedRecord.type : 'unknown',
        messageContent: decodedMessage,
      };

      const result = await this.processMessage(
        topic,
        parsedRecord,
        libp2pNode,
        heliaNode
      );

      if (result !== null) {
        onMessage(result, stats);
      }
    };

    for (const topic of topics) {
      const unsubscribe = this.libp2pService.subscribeToTopic(libp2pNode, topic, (data) => {
        void handleIncomingMessage(data, topic);
      });
      unsubscribeFunctions.push(unsubscribe);
    }

    return () => {
      this.log.info('Cleaning up messaging subscriptions');
      unsubscribeFunctions.forEach((unsubscribe) => {
        unsubscribe();
      });
    };
  }

  async processMessage(
    topic: string,
    message: MessagePayload,
    libp2pNode: LibP2pWithServices,
    heliaNode: HeliaNode | null
  ): Promise<MessageHandlerResult | null> {
    const myPeerId = libp2pNode.peerId.toString();
    if (message.sender === myPeerId) {
      return null;
    }

    if (
      message.targetPeerId != null &&
      message.targetPeerId.length > 0 &&
      message.targetPeerId !== myPeerId
    ) {
      return null;
    }

    if (isFolderCreatedMessage(message)) {
      const data: FolderCreatedResult = {
        sixId: message.sixId,
        folderName: message.folderName,
        folderPath: message.folderPath,
        sender: message.sender,
        messageTimestamp: message.timestamp,
      };

      return { type: 'folder-created', data };
    }

    if (isFolderDeletedMessage(message)) {
      const data: FolderDeletedResult = {
        sixId: message.sixId,
        folderPath: message.folderPath,
        sender: message.sender,
        messageTimestamp: message.timestamp,
      };

      return { type: 'folder-deleted', data };
    }

    if (isFileDeletedMessage(message)) {
      const data: FileDeletedResult = {
        sixId: message.sixId,
        fileId: message.fileId,
        sender: message.sender,
        messageTimestamp: message.timestamp,
      };

      return { type: 'file-deleted', data };
    }

    if (isSixCreatedMessage(message)) {
      const data: SixCreatedResult = {
        sender: message.sender,
        messageTimestamp: message.timestamp,
      };

      return { type: 'six-created', data };
    }

    if (isSixUpdatedMessage(message)) {
      const data: SixUpdatedResult = {
        sender: message.sender,
        messageTimestamp: message.timestamp,
      };

      return { type: 'six-updated', data };
    }

    if (isSixDeletedMessage(message)) {
      const data: SixDeletedResult = {
        sender: message.sender,
        messageTimestamp: message.timestamp,
      };

      return { type: 'six-deleted', data };
    }

    const handler = this.handlers.find((candidate) => candidate.canHandle(topic, message));
    if (handler === undefined) {
      this.log.debug(`No handler found for message type ${message.type} on topic ${topic}`);
      return null;
    }

    return await handler.process(message, libp2pNode, heliaNode);
  }

  async publishMessage(
    libp2pNode: LibP2pWithServices,
    topic: string,
    message: MessagePayload
  ): Promise<void> {
    const enrichedMessage: MessagePayload = {
      ...message,
      sender: libp2pNode.peerId.toString(),
      timestamp: Date.now(),
    };

    await this.libp2pService.publishToTopic(libp2pNode, topic, JSON.stringify(enrichedMessage));
  }

  async publishTopicList(
    libp2pNode: LibP2pWithServices,
    sixId: string,
    topics: string[]
  ): Promise<void> {
    const topicListMessage: MessagePayload = {
      type: 'topic-list',
      id: crypto.randomUUID(),
      sender: libp2pNode.peerId.toString(),
      timestamp: Date.now(),
      topics,
      sixId,
    };

    await this.libp2pService.publishToTopic(
      libp2pNode,
      TOPIC_LIST_TOPIC,
      JSON.stringify(topicListMessage)
    );
  }

  getSupportedTopics(): string[] {
    return [...this.topics];
  }
}
